
import React, { useState, useEffect } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { sdgsData } from "@/data/sdgs";

export const Hero: React.FC = () => {
  const { t, language } = useLanguage();
  const [featuredSDG, setFeaturedSDG] = useState(sdgsData[0]);

  // Select a random SDG on initial load
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * sdgsData.length);
    setFeaturedSDG(sdgsData[randomIndex]);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      {/* Background with overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/images/hero-bg.jpg')" }}
      >
        <div className="absolute inset-0 bg-palsustain-green/50 backdrop-blur-sm"></div>
      </div>

      <div className="container mx-auto px-4 z-10 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Left Column - Current Hero Content */}
          <div className={`text-center md:text-left ${language === "ar" ? "md:order-2" : ""}`}>
            <div className="inline-block px-4 py-1 mb-6 bg-white/10 backdrop-blur-md rounded-full text-white font-medium animate-fade-in">
              <span>Palsustain</span>
            </div>
            
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight animate-fade-in" style={{ animationDelay: "0.2s" }}>
              {t("home.hero.title")}
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 mb-8 animate-fade-in" style={{ animationDelay: "0.4s" }}>
              {t("home.hero.subtitle")}
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-4 animate-fade-in" style={{ animationDelay: "0.6s" }}>
              <Link 
                to="/sdgs"
                className="px-8 py-3 bg-palsustain-cream text-palsustain-green font-medium rounded-md hover:bg-white transition-all duration-300 flex items-center justify-center"
              >
                {t("home.hero.cta")}
                <ArrowRight className={`ml-2 h-4 w-4 ${language === "ar" ? "transform rotate-180" : ""}`} />
              </Link>
            </div>
          </div>
          
          {/* Right Column - Featured SDG */}
          <div className={`rounded-xl overflow-hidden shadow-lg animate-fade-in ${language === "ar" ? "md:order-1" : ""}`} style={{ animationDelay: "0.5s" }}>
            <div className="bg-white/90 backdrop-blur-md p-6 md:p-8">
              <div className="flex items-center gap-4 mb-4">
                <div 
                  className="w-12 h-12 flex-shrink-0 rounded-full flex items-center justify-center" 
                  style={{ backgroundColor: `${featuredSDG.color}15` }}
                >
                  <img 
                    src={featuredSDG.icon || `/placeholder.svg`} 
                    alt={language === "en" ? featuredSDG.title.en : featuredSDG.title.ar}
                    className="w-10 h-10 object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        parent.textContent = featuredSDG.id.toString();
                      }
                    }}
                  />
                </div>
                <div>
                  <span 
                    className="text-xs font-medium text-white px-2 py-1 rounded-full mb-1 inline-block" 
                    style={{ backgroundColor: featuredSDG.color }}
                  >
                    SDG {featuredSDG.id}
                  </span>
                  <h3 className="text-lg font-bold text-palsustain-charcoal">
                    {language === "en" ? featuredSDG.title.en : featuredSDG.title.ar}
                  </h3>
                </div>
              </div>
              
              <h4 className="text-lg font-medium text-palsustain-green mb-3">
                {language === "en" ? "In Palestinian Context:" : "في السياق الفلسطيني:"}
              </h4>
              
              <p className="text-palsustain-charcoal/80 mb-6">
                {language === "en" ? featuredSDG.palestinianContext.en : featuredSDG.palestinianContext.ar}
              </p>
              
              <Link 
                to={`/sdgs/${featuredSDG.slug}`}
                className="inline-flex items-center text-palsustain-green font-medium hover:underline"
              >
                {language === "en" ? "Learn more about this goal" : "تعرف على المزيد حول هذا الهدف"}
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
